"use client"

import { useSidebarContext } from "./hooks/useSidebarContext"
import { FluidHoverIndicator } from "@/components/fluid-hover-indicator"
import { FluidHoverIndicatorV2 } from "@/components/fluid-hover-indicator-v2"

/**
 * Props del SidebarFluidIndicator
 * @interface
 */
interface SidebarFluidIndicatorProps {
  /** Elemento del item actualmente hovered/focused */
  targetElement: HTMLElement | null
  /** Indica si el indicador está visible */
  isVisible: boolean
}

/**
 * Componente wrapper de FluidHoverIndicator para Sidebar
 *
 * @description
 * Wrapper que conecta el FluidHoverIndicator (glassmorphism + spring physics)
 * con el sistema de estado del Sidebar. Este componente decide automáticamente
 * qué versión renderizar basándose en las feature flags:
 *
 * - **V1 (default)**: FluidHoverIndicator con backdrop-blur CSS básico
 * - **V2 (experimental)**: FluidHoverIndicatorV2 con SVG displacement mapping
 *
 * Feature flags del contexto:
 * - `liquidGlass.enableLiquidGlassV2`: Activa la versión V2
 * - `liquidGlass.enableChromaticAberration`: Activa chromatic aberration (Phase 2)
 *
 * @example
 * ```tsx
 * <SidebarNav>
 *   <SidebarIndicator />
 *   <SidebarFluidIndicator targetElement={currentItemElement} isVisible={isVisible} />
 *   <SidebarContent>...</SidebarContent>
 * </SidebarNav>
 * ```
 */
export function SidebarFluidIndicator({ targetElement, isVisible }: SidebarFluidIndicatorProps) {
  const { navRef, liquidGlass } = useSidebarContext()

  // Decidir qué versión renderizar basándose en feature flags
  if (liquidGlass.enableLiquidGlassV2) {
    return (
      <FluidHoverIndicatorV2
        targetElement={targetElement}
        isVisible={isVisible}
        containerRef={navRef as React.RefObject<HTMLElement>}
        enableChromaticAberration={liquidGlass.enableChromaticAberration}
        className="z-[5]"
      />
    )
  }

  // Fallback a versión V1 (CSS blur básico)
  return (
    <FluidHoverIndicator
      targetElement={targetElement}
      isVisible={isVisible}
      containerRef={navRef as React.RefObject<HTMLElement>}
      className="z-[5]"
    />
  )
}
